import * as React from 'react'

import { Box, CollapsableCard, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import { FormikProps } from 'formik'

import { observer } from 'mobx-react'

import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import AutomaticBreakSettings from 'modules/attendance/components/SettingItemPage/TimeTracking/BreakSettings/AutomaticBreakSettings'
import AutomaticRuleForm from 'modules/attendance/components/SettingItemPage/TimeTracking/BreakSettings/AutomaticBreakSettings/AutomaticRules/form'
import { BreakSettingsType } from 'modules/attendance/hooks/useFetchBreakSettings'
import { TimeSettingsBreakConfigs } from 'modules/attendance/hooks/useFetchTimeSettingsBreakConfigurations'
import { BreakSettingsFormValues } from 'modules/attendance/lib/forms/BreakSettingForm'
import { useTimeTrackingFeatureAvailability } from 'modules/attendance/lib/useTimeTrackingFeatureAvailability'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'

interface Props {
  attendanceSetting: AttendanceSetting
  breakSetting: BreakSettingsType | undefined
  timeSettingsBreakConfigurations: TimeSettingsBreakConfigs[]
  settingForm: FormikProps<BreakSettingsFormValues>
  refetch: () => void
}

const AutomaticBreak = ({
  attendanceSetting,
  breakSetting,
  timeSettingsBreakConfigurations,
  settingForm,
  refetch,
}: Props) => {
  const automaticBreaksFeatureEnabled = useTimeTrackingFeatureAvailability(
    'time_tracking_automatic_breaks'
  )
  const attendanceSettingBreakEnabled = attendanceSetting?.hasBreaksEnabled
  const automaticBreakValue = settingForm.values.automaticBreaks

  const [automaticBreaksEnabled, setAutomaticBreaksEnabled] = React.useState(
    (attendanceSettingBreakEnabled && automaticBreakValue) ?? false
  )
  const automaticBreakRules = breakSetting?.attendanceAutomaticBreakRules || []
  const [automaticRuleForms, setAutomaticRuleForms] = React.useState<AutomaticRuleForm[]>(
    automaticBreakRules.map((rule) => new AutomaticRuleForm(rule))
  )
  const addAutomaticRule = () =>
    setAutomaticRuleForms([...automaticRuleForms, new AutomaticRuleForm()])

  const resetAutomaticRuleForms = () => {
    automaticRuleForms.forEach((form) => form.cleanAll())

    const validRules = automaticRuleForms.filter((form) => !form.get('_destroy').value)

    setAutomaticRuleForms([...validRules])
  }

  const { running: submitting, performAction: onConfirm } = useAsyncAction(async () => {
    settingForm.handleSubmit()
    resetAutomaticRuleForms()
  })

  const onSave = async () => {
    settingForm.setFieldValue(
      'automaticBreakRulesAttributes',
      automaticRuleForms.filter((rule) => rule.valid()).map((rule) => rule.data())
    )

    onConfirm().then((data) => {
      if (data.success) {
        refetch()
        resetAutomaticRuleForms()
      }
    })
  }

  const handleAutomaticBreaksToggle = async (updatedValue: boolean) => {
    settingForm.setFieldValue('automaticBreaks', updatedValue)

    if (updatedValue) {
      settingForm.setFieldValue('manualBreaks', false)
      settingForm.setFieldValue('restTime', false)
    }

    if (!attendanceSetting?.hasBreaksEnabled && updatedValue) {
      await attendanceSetting.update({ enable_breaks: true })
    }

    if (automaticRuleForms.length === 0 && updatedValue) {
      addAutomaticRule()
      return
    }

    resetAutomaticRuleForms()

    onConfirm().then((data) => {
      if (data.success) {
        setAutomaticBreaksEnabled(updatedValue && attendanceSetting?.hasBreaksEnabled)
        refetch()
      }
    })
  }

  useEffect(
    () => {
      setAutomaticBreaksEnabled(
        attendanceSetting?.hasBreaksEnabled && settingForm.values.automaticBreaks
      )
      if (!settingForm.values.automaticBreaks) {
        resetAutomaticRuleForms()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attendanceSetting?.hasBreaksEnabled, settingForm.values.automaticBreaks]
  )

  return (
    <>
      {automaticBreaksFeatureEnabled && (
        <CollapsableCard
          open={automaticBreaksEnabled}
          Card={
            <Box gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal weight="medium">
                  {i18n.t('time_tracking.settings.breaks.instance.automatic_breaks.label')}
                </Text.Normal>
                <TogglerCheckbox
                  value={automaticBreaksEnabled}
                  onChange={handleAutomaticBreaksToggle}
                  disabled={submitting}
                  labelBefore
                />
              </Box>
              <Text.Normal color="grey800" htmlTag="div">
                {i18n.t('time_tracking.settings.breaks.instance.automatic_breaks.description')}
              </Text.Normal>
              <HelpCenterLink name="automatic_breaks">
                {i18n.t('time_tracking.settings.breaks.learn_more')}
              </HelpCenterLink>
            </Box>
          }
          Content={
            <AutomaticBreakSettings
              settingForm={settingForm}
              automaticRuleForms={automaticRuleForms}
              addAutomaticRule={addAutomaticRule}
              timeSettingsBreakConfigurations={timeSettingsBreakConfigurations}
              onConfirm={onSave}
              attendanceSettingId={attendanceSetting.id}
            />
          }
        />
      )}
    </>
  )
}

export default observer(AutomaticBreak)
